import { useCallback, useEffect, useRef, useState } from 'react';
import { MessageSquare, Mail, StickyNote, Pin, AlarmClock, Plus, Loader2, AlertCircle, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { commsFilesAPI, errMsg, fmtFull } from '../../lib/communicationsAPI';
import { RichTextEditor } from '../comms/RichTextEditor';
import { RichBody } from '../comms/RichBody';
import { AttachmentList } from '../comms/Attachments';
import { StartConversationDialog } from '../comms/StartConversationDialog';
import { NewEmailDialog } from '../comms/NewEmailDialog';
import { useMailboxes } from '../../hooks/useComms';

const KINDS = [['all', 'All'], ['file_notes', 'File notes'], ['portal', 'Portal'], ['email', 'Email'], ['compliance', 'Compliance reminders']];
const META = {
  file_note: { icon: FileText, label: 'File note', cls: 'border-l-slate-400' },
  message: { icon: MessageSquare, label: 'Portal message', cls: 'border-l-primary' },
  conversation_note: { icon: StickyNote, label: 'Internal note', cls: 'border-l-amber-400 bg-amber-50/60 dark:bg-amber-950/20' },
  email: { icon: Mail, label: 'Email', cls: 'border-l-cyan-500' },
  follow_up: { icon: AlarmClock, label: 'Follow-up', cls: 'border-l-orange-400' },
  compliance_reminder: { icon: AlarmClock, label: 'Compliance reminder', cls: 'border-l-indigo-400' },
};

const Item = ({ it, onOpen }) => {
  const m = META[it.kind];
  const isEmail = it.kind === 'message' && it.channel === 'email';
  const label = isEmail ? (it.direction === 'inbound' ? 'Email received' : 'Email sent') : it.kind === 'message' ? (it.direction === 'inbound' ? 'Client message' : 'Firm reply') : m.label;
  const clickable = !!it.conversation_id;
  return (
    <div className={cn('rounded-lg border border-border border-l-4 px-4 py-3', m.cls, clickable && 'cursor-pointer hover:bg-muted/40')} data-testid={`timeline-item-${it.kind}-${it.id}`}
      onClick={() => clickable && onOpen(it.conversation_id)} role={clickable ? 'link' : undefined}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {it.pinned ? <Pin className="w-3.5 h-3.5 text-primary" /> : isEmail ? <Mail className="w-3.5 h-3.5 text-cyan-600" /> : <m.icon className="w-3.5 h-3.5" />}
        <span className="font-semibold text-foreground/80">{label}</span>
        {it.subject && <span className="truncate">· {it.subject}</span>}
        <span className="ml-auto shrink-0">{it.author_name ? `${it.author_name} · ` : ''}{fmtFull(it.at)}</span>
      </div>
      {it.body && <RichBody body={it.body} format={it.body_format} className="mt-1 text-foreground" />}
      {it.kind === 'email' && <p className="text-sm mt-1">To {it.to}</p>}
      {isEmail && <p className="text-xs text-muted-foreground mt-1" data-testid={`timeline-email-meta-${it.id}`}>From {it.sender_address}{it.email_to?.length ? ` · To ${it.email_to.join(', ')}` : ''}</p>}
      {it.kind === 'compliance_reminder' && <p className="text-xs text-muted-foreground mt-1">{it.channel} · {it.status}</p>}
      {it.kind === 'follow_up' && <p className="text-sm mt-1">Follow-up due {it.at?.slice(0, 10)}</p>}
      {it.linked_task && <p className="text-xs text-muted-foreground mt-1">Linked task: {it.linked_task.title} ({it.linked_task.status})</p>}
      {it.attachments?.length > 0 && <div onClick={(e) => e.stopPropagation()}><AttachmentList items={it.attachments} staff /></div>}
    </div>
  );
};

// Client record → Timeline: file notes (pinned first), portal messages, internal notes, emails, follow-ups.
export function ClientTimeline({ clientId, canEdit }) {
  const navigate = useNavigate();
  const [kind, setKind] = useState('all');
  const [range, setRange] = useState({ date_from: '', date_to: '' });
  const [data, setData] = useState({ pinned: [], items: [], next: null, loading: true, error: null });
  const [adding, setAdding] = useState(false);
  const [noteHtml, setNoteHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const mailboxes = useMailboxes(!!canEdit);
  const editorRef = useRef(null);

  const params = useCallback((cursor) => ({ kind, limit: 25, ...(range.date_from && { date_from: range.date_from }), ...(range.date_to && { date_to: range.date_to }), ...(cursor && { cursor }) }), [kind, range]);
  const load = useCallback(async () => {
    setData((d) => ({ ...d, loading: true, error: null }));
    try {
      const r = await commsFilesAPI.timeline(clientId, params());
      setData({ pinned: r.data.pinned, items: r.data.items, next: r.data.next_cursor, loading: false, error: null });
    } catch (e) { setData((d) => ({ ...d, loading: false, error: errMsg(e, 'Failed to load timeline') })); }
  }, [clientId, params]);
  useEffect(() => { load(); }, [load]);
  const more = async () => {
    const r = await commsFilesAPI.timeline(clientId, params(data.next));
    setData((d) => ({ ...d, items: [...d.items, ...r.data.items], next: r.data.next_cursor }));
  };
  const saveNote = async () => {
    setSaving(true);
    try { await commsFilesAPI.addFileNote(clientId, noteHtml); toast.success('File note added'); editorRef.current?.clear(); setNoteHtml(''); setAdding(false); load(); } catch (e) { toast.error(errMsg(e, 'Failed to add note')); } finally { setSaving(false); }
  };
  const open = (cid) => navigate(`/comms/inbox?c=${cid}`);

  return (
    <div className="space-y-4" data-testid="client-timeline">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-headings text-lg font-semibold text-foreground mr-auto">Timeline</h3>
        {canEdit && <Button size="sm" variant="outline" onClick={() => setAdding((a) => !a)} data-testid="timeline-add-file-note"><Plus className="w-4 h-4 mr-1.5" />Add file note</Button>}
        {canEdit && <Button size="sm" onClick={() => setStartOpen(true)} data-testid="timeline-message-client"><MessageSquare className="w-4 h-4 mr-1.5" />Message client</Button>}
        {canEdit && mailboxes.length > 0 ? (
          <Button size="sm" variant="outline" onClick={() => setEmailOpen(true)} data-testid="timeline-email-btn"><Mail className="w-4 h-4 mr-1.5" />Email</Button>
        ) : (
          <TooltipProvider><Tooltip><TooltipTrigger asChild>
            <span tabIndex={0}><Button size="sm" variant="outline" disabled aria-label="Email (connect a mailbox in Settings → Communications to email clients)" data-testid="timeline-email-disabled"><Mail className="w-4 h-4 mr-1.5" />Email</Button></span>
          </TooltipTrigger><TooltipContent>Connect a mailbox in Settings → Communications to email clients</TooltipContent></Tooltip></TooltipProvider>
        )}
      </div>
      {adding && (
        <div className="space-y-2 rounded-lg border border-border p-3" data-testid="timeline-file-note-form">
          <RichTextEditor ref={editorRef} onChange={setNoteHtml} onSubmit={saveNote} placeholder="Internal file note (staff only — not shown to the client or in the inbox)" testId="timeline-file-note-input" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" onClick={saveNote} disabled={saving || !noteHtml} data-testid="timeline-file-note-save">{saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save note</Button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {KINDS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setKind(k)} aria-pressed={kind === k} data-testid={`timeline-filter-${k}`}
            className={cn('px-3 py-1 rounded-full text-xs border transition-colors', kind === k ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted')}>{l}</button>
        ))}
        <Input type="date" className="h-8 w-36 ml-auto" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} aria-label="From date" data-testid="timeline-date-from" />
        <Input type="date" className="h-8 w-36" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} aria-label="To date" data-testid="timeline-date-to" />
      </div>
      {data.error ? <p className="text-sm text-destructive flex items-center gap-2" data-testid="timeline-error"><AlertCircle className="w-4 h-4" />{data.error}</p>
        : data.loading ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          : (data.pinned.length + data.items.length) === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="timeline-empty">No activity yet.</p>
            : (
              <div className="space-y-2">
                {data.pinned.map((it) => <Item key={`p-${it.id}`} it={it} onOpen={open} />)}
                {data.items.map((it) => <Item key={`${it.kind}-${it.id}`} it={it} onOpen={open} />)}
                {data.next && <div className="text-center"><Button size="sm" variant="ghost" onClick={more} data-testid="timeline-load-more">Load more</Button></div>}
              </div>
            )}
      <NewEmailDialog open={emailOpen} clientId={clientId} mailboxes={mailboxes} onClose={() => setEmailOpen(false)} onSent={(id) => { setEmailOpen(false); load(); if (id) open(id); }} />
      <StartConversationDialog open={startOpen} defaultClientId={clientId} onClose={() => setStartOpen(false)} onCreated={(id) => { setStartOpen(false); open(id); }} />
    </div>
  );
}
